import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from 'jotai';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/useToast';
import { deleteUserAccount } from '@/lib/apis';
import { clearAllUserData } from '@/lib/auth-cleanup';
import { deleteAccessTokenCookie, defaultProfile } from '@/lib/auth';
import { useUserProfile } from '@/contexts/user.context';
import { usePostHog } from 'posthog-js/react';

export function CancelAccount() {
  const posthog = usePostHog();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const store = useStore();
  const { setUser } = useUserProfile();

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteUserAccount();
      posthog?.capture('account_deleted');

      toast({
        title: 'Account Deleted',
        description: result.message,
      });

      // Clear all user data and log out
      deleteAccessTokenCookie();
      setUser(defaultProfile);
      clearAllUserData(queryClient, store);

      // Navigate to signin page
      navigate({ to: '/signin' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete account',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setIsDialogOpen(false);
    }
  };

  return (
    <>
      <Card className='rounded-lg border bg-white'>
        <CardContent className='p-6'>
          <div className='flex items-center justify-between'>
            <div>
              <h3 className='text-gray-900 font-medium'>Cancel My Account</h3>
              <p className='text-gray-700 text-sm'>Permanently delete your account and all associated data.</p>
            </div>
            <Button
              variant='outline'
              onClick={() => setIsDialogOpen(true)}
              className='text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700'
            >
              Cancel Account
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent className='max-w-md'>
          <AlertDialogHeader>
            <AlertDialogTitle className='flex items-center gap-2 text-gray-900'>
              <AlertCircle className='h-5 w-5 text-red-600' />
              Cancel your account?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Cancelling your account pauses your campaigns in GlitchAds and cannot be undone. Review the details below
              before confirming.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className='space-y-4 py-2'>
            <p className='text-gray-700'>Before you proceed, please note:</p>
            <ul className='space-y-3 text-sm text-gray-700'>
              <li className='flex gap-2'>
                <span className='text-red-500 mt-1'>•</span>
                <span>
                  <strong className='text-gray-900'>Check your Google Ads account</strong> - Your campaigns will be
                  paused in GlitchAds, but you should verify they are properly managed in your Google Ads dashboard.
                </span>
              </li>
              <li className='flex gap-2'>
                <span className='text-red-500 mt-1'>•</span>
                <span>
                  If you are the only member of an organization, all campaigns in that organization will be
                  automatically paused.
                </span>
              </li>
              <li className='flex gap-2'>
                <span className='text-red-500 mt-1'>•</span>
                <span>
                  This action cannot be undone. Your account data will be permanently deleted.
                </span>
              </li>
            </ul>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Keep Account
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              className='bg-red-600 hover:bg-red-700 focus:ring-red-600'
            >
              {isDeleting ? 'Deleting...' : 'Delete Account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
